import React from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { AxiosError, AxiosResponse } from "axios";
import { BiCheck, BiX } from "react-icons/bi";
import { HiCheck } from "react-icons/hi";
import { CustomButton } from "../atoms/CustomButton";
import BsInputPassword from "../public/BsInputPassword";
import PasswordValidator from "../public/PasswordValidator";
import { useUserResetPassword } from "../../hooks/users";
import { useCheckPassword } from "../../hooks/auth";
import {
  PasswordLevelRules,
  PasswordRule,
  meetsPasswordRules,
} from "../../hooks/usePasswordResetContext";

export interface ResetPasswordFormLabels {
  newPasswordLabel: string;
  newPasswordPlaceholder: string;
  confirmPasswordLabel: string;
  confirmPasswordPlaceholder: string;
  submitLabel: string;
  matchLabel: string;
  passwordMismatchError: string;
}

interface ResetPasswordFormValues {
  new_password: string;
  new_confirm_password: string;
}

interface ResetPasswordFormProps {
  entityId: string;
  userId: string;
  token: string;
  passwordLevel: string | null;
  passwordRules: PasswordRule[];
  acceptedLevels?: PasswordLevelRules[];
  levelHint?: string | null;
  labels: ResetPasswordFormLabels;
  onSuccess: (response: AxiosResponse<any>) => void;
}

const buildSchema = (labels: ResetPasswordFormLabels) =>
  Joi.object().keys({
    new_password: Joi.string().trim().required().label(labels.newPasswordLabel),
    new_confirm_password: Joi.string()
      .valid(Joi.ref("new_password"))
      .required()
      .messages({
        "any.only": labels.passwordMismatchError,
      })
      .label(labels.confirmPasswordLabel),
  });

const toValidatorRuleSet = (
  rules: PasswordRule[],
  hintOverride?: string | null
) =>
  rules.map((rule) => ({
    regex: rule.regex,
    message: hintOverride ?? rule.description,
  }));

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  entityId,
  userId,
  token,
  passwordLevel,
  passwordRules,
  acceptedLevels,
  levelHint,
  labels,
  onSuccess,
}) => {
  const { mutate: resetPassword, isLoading: isReseting } =
    useUserResetPassword();
  const { mutate: checkPassword, isLoading: isChecking } = useCheckPassword();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: joiResolver(buildSchema(labels)),
    mode: "onChange",
  });

  const newPasswordValue = watch("new_password") ?? "";
  const confirmPasswordValue = watch("new_confirm_password") ?? "";
  const passwordsMatch =
    newPasswordValue.length > 0 && newPasswordValue === confirmPasswordValue;

  const validatorRuleSet = toValidatorRuleSet(passwordRules, levelHint);

  const onSubmit = (data: ResetPasswordFormValues) => {
    checkPassword(
      {
        password: data.new_password,
        ...(passwordLevel ? { level: passwordLevel } : {}),
      },
      {
        onSuccess: () => {
          resetPassword(
            { entityId, userId, token, data },
            { onSuccess: (response) => onSuccess(response) }
          );
        },
        onError: (err: AxiosError<any, any>) => {
          const description =
            err.response?.data?.message ||
            err.response?.data?.description ||
            (err.message as string);
          setError("new_password", { type: "manual", message: description });
        },
      }
    );
  };

  const isSubmitting = isChecking || isReseting;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-5"
      autoComplete="off"
      noValidate
    >
      <div className="flex flex-col gap-2">
        <BsInputPassword
          label={labels.newPasswordLabel}
          placeholder={labels.newPasswordPlaceholder}
          isInvalid={!!errors.new_password}
          errorMessage={errors.new_password?.message?.toString()}
          rhfControls={register("new_password")}
          rest={{ autoFocus: true, autoComplete: "new-password" }}
        />
        {newPasswordValue.length > 0 && validatorRuleSet.length > 0 && (
          <PasswordValidator
            className="text-xs mt-1"
            ruleSet={validatorRuleSet}
            passwordValue={newPasswordValue}
            passwordAccepted={meetsPasswordRules(
              newPasswordValue,
              { level: passwordLevel ?? "", rules: passwordRules },
              acceptedLevels
            )}
            passedRuleClassName="flex items-center text-green-600 gap-1"
            failedRuleClassName="flex items-center text-gray-500 gap-1"
            passIcon={<BiCheck size={16} />}
            failIcon={<BiX size={16} />}
          />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <BsInputPassword
          label={labels.confirmPasswordLabel}
          placeholder={labels.confirmPasswordPlaceholder}
          isInvalid={!!errors.new_confirm_password}
          errorMessage={errors.new_confirm_password?.message?.toString()}
          rhfControls={register("new_confirm_password")}
          rest={{ autoComplete: "new-password" }}
        />
        {passwordsMatch && !errors.new_confirm_password && (
          <p className="flex items-center gap-1 text-xs text-green-600 font-figtree">
            <HiCheck size={14} />
            {labels.matchLabel}
          </p>
        )}
      </div>

      <CustomButton
        type="submit"
        variant="primary"
        className="w-full mt-2"
        disabled={isSubmitting}
        isLoading={isSubmitting}
        title={labels.submitLabel}
      />
    </form>
  );
};

export default ResetPasswordForm;
